import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ChevronDown, Mic, Sparkles, PenSquare, BookOpen,
  ShieldCheck, Flame, CalendarDays, Users, Quote,
} from "lucide-react";
import MinutesArtifact from "../components/MinutesArtifact";

const openAuth = () =>
  window.dispatchEvent(
    new CustomEvent("boardroom:open-auth", { detail: { view: "signup" } })
  );

const RITUAL = [
  { numeral: "I", icon: CalendarDays, title: "Book the slot", body: "Weekly stand-up, monthly review. It lands in your calendar with a reminder." },
  { numeral: "II", icon: Mic, title: "Hold the session", body: "Solo or with a partner on live video. Talk it through — Boardroom records." },
  { numeral: "III", icon: Sparkles, title: "Get the minutes", body: "Transcribed and written up: wins, losses, decisions, next moves." },
  { numeral: "IV", icon: PenSquare, title: "Sign it off", body: "Every commitment is dated. Next week opens with last week's promises." },
];

const PROOF = [
  { icon: BookOpen, label: "Board Books", body: "Quarter and year reviews compiled from your own sessions — shareable, printable." },
  { icon: Flame, label: "Streaks", body: "Weeks running and on-time rate, so the rhythm is visible even on the bad weeks." },
  { icon: ShieldCheck, label: "Private by default", body: "Your recordings and minutes are yours. Sharing is opt-in, one session at a time." },
];

const FAQ = [
  { q: "Does it work if I'm on my own?", a: "That's the default. Most sessions are solo — you, the agenda and the record. A partner is optional." },
  { q: "Do I have to record myself?", a: "No. You can type into each section. If you do record, it's transcribed and folded into the minutes so nothing is lost." },
  { q: "How long does a session take?", a: "About 15 minutes for a weekly. The monthly and quarterly reviews run longer because they roll up everything before them." },
  { q: "What does it cost?", a: "Free to start, with a 7-day Pro trial on the deeper AI features. No card to create an account." },
];

/**
 * Phone-first marketing landing page — shown at `/` to signed-out visitors
 * with no sessions on the device. One primary call to action (create account)
 * repeated in the hero, mid-page and a sticky mobile bar.
 */
export default function Landing() {
  const [openFaq, setOpenFaq] = useState(-1);

  return (
    <div className="pb-28 md:pb-4" data-testid="landing-page">
      {/* ---------------- Hero ---------------- */}
      <section className="flex flex-col lg:flex-row lg:items-center gap-12 lg:gap-16">
        <div className="max-w-2xl">
          <p className="label-eyebrow text-[#c89f45]">BOARDROOM · PERSONAL ACCOUNTABILITY</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.08] tracking-tight">
            Hold yourself to the standard you'd hold a company to.
          </h1>
          <p className="mt-6 text-[#bbb] text-base md:text-lg leading-relaxed">
            One recorded session a week. Boardroom writes the minutes, tracks
            what you promised, and compiles the quarter into a board book you
            can actually read back.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <button
              type="button"
              onClick={openAuth}
              className="w-full sm:w-auto h-12 px-6 rounded-md bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="landing-hero-cta"
            >
              Create your free account
              <ArrowUpRight size={16} />
            </button>
            <Link
              to="/sample"
              className="w-full sm:w-auto h-12 px-6 rounded-md border border-[#2a2a2a] text-[#eaeaea] hover:border-[#c89f45] hover:text-[#c89f45] transition-colors flex items-center justify-center gap-2 text-sm font-medium"
              data-testid="landing-hero-sample"
            >
              See a real session
            </Link>
          </div>
          <p className="mt-4 text-xs text-[#8a8a8a] font-mono uppercase tracking-widest">
            Free to start · 7-day Pro trial · no card
          </p>
        </div>

        <div className="w-full lg:w-[340px] lg:shrink-0 flex justify-center">
          <MinutesArtifact testId="landing-minutes-artifact" />
        </div>
      </section>

      <div className="gold-divider my-14 sm:my-20" />

      {/* ---------------- The ritual ---------------- */}
      <section data-testid="landing-ritual">
        <p className="label-eyebrow text-[#c89f45]">THE RITUAL</p>
        <h2 className="font-serif text-2xl sm:text-3xl mt-3 leading-snug max-w-xl">
          Four moves. Fifteen minutes. A year of receipts.
        </h2>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {RITUAL.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.numeral}
                className="surface rounded-md p-5 transition-colors hover:border-[#c89f45]/50"
                data-testid={`landing-step-${i + 1}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-serif text-2xl text-[#c89f45] leading-none">{s.numeral}</span>
                  <Icon size={16} className="text-[#8a8a8a]" strokeWidth={1.5} />
                </div>
                <p className="font-serif text-lg mt-4 leading-snug">{s.title}</p>
                <p className="text-sm text-[#9a9a9a] mt-2 leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------------- Proof ---------------- */}
      <section className="mt-14 sm:mt-20" data-testid="landing-proof">
        <p className="label-eyebrow text-[#c89f45]">THE PAPER TRAIL</p>
        <h2 className="font-serif text-2xl sm:text-3xl mt-3 leading-snug max-w-xl">
          Proof you showed up, not just an app you opened.
        </h2>
        <div className="mt-8 space-y-3 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4">
          {PROOF.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.label} className="surface rounded-md p-5" data-testid={`landing-proof-${i + 1}`}>
                <Icon size={18} className="text-[#c89f45]" strokeWidth={1.5} />
                <p className="font-serif text-lg mt-3">{p.label}</p>
                <p className="text-sm text-[#9a9a9a] mt-2 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-6 surface rounded-md p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <Users size={18} className="text-[#c89f45] shrink-0" strokeWidth={1.5} />
          <p className="text-sm text-[#bbb] leading-relaxed flex-1">
            Members publish sessions to the Founders wall — read what other
            people put on the record before you write your own.
          </p>
          <Link
            to="/founders"
            className="h-11 px-4 rounded-md border border-[#2a2a2a] text-sm text-[#eaeaea] hover:border-[#c89f45] hover:text-[#c89f45] transition-colors inline-flex items-center justify-center shrink-0"
            data-testid="landing-founders-link"
          >
            Founders wall
          </Link>
        </div>
      </section>

      {/* ---------------- Member quote ---------------- */}
      <section className="mt-14 sm:mt-20" data-testid="landing-testimonials">
        <p className="label-eyebrow text-[#c89f45]">FROM A MEMBER</p>
        <blockquote className="surface rounded-md p-6 sm:p-8 mt-4">
          <Quote size={20} className="text-[#c89f45]" strokeWidth={1.5} />
          <p className="font-serif text-xl sm:text-2xl leading-snug mt-4">
            "This is an amazing tool that allows me to check in with myself to
            keep me motivated and accountable."
          </p>
          <footer className="mt-5 text-xs font-mono uppercase tracking-widest text-[#8a8a8a]">
            Hailey F · Boardroom member
          </footer>
        </blockquote>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section className="mt-14 sm:mt-20" data-testid="landing-faq">
        <p className="label-eyebrow text-[#c89f45]">BEFORE YOU SIGN UP</p>
        <h2 className="font-serif text-2xl sm:text-3xl mt-3 leading-snug">Fair questions.</h2>
        <ul className="mt-6 space-y-3">
          {FAQ.map((item, i) => {
            const isOpen = openFaq === i;
            return (
              <li key={i} className="surface rounded-md overflow-hidden" data-testid={`landing-faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full min-h-[56px] flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-[#181818] transition-colors"
                  data-testid={`landing-faq-question-${i}`}
                >
                  <span className="font-serif text-base sm:text-lg">{item.q}</span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 text-[#8a8a8a] transition-transform ${isOpen ? "rotate-180 text-[#c89f45]" : ""}`}
                  />
                </button>
                {isOpen && (
                  <p className="px-5 pb-5 text-sm text-[#bbb] leading-relaxed" data-testid={`landing-faq-answer-${i}`}>
                    {item.a}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
        <Link
          to="/faq"
          className="mt-4 inline-flex items-center gap-1.5 min-h-[44px] text-sm text-[#c89f45] hover:underline"
          data-testid="landing-faq-all"
        >
          Read the full FAQ <ArrowUpRight size={14} />
        </Link>
      </section>

      {/* ---------------- Closing CTA ---------------- */}
      <section className="mt-14 sm:mt-20 surface rounded-md p-6 sm:p-10" data-testid="landing-closing-cta">
        <h2 className="font-serif text-2xl sm:text-3xl leading-snug max-w-lg">
          Your first session can be tonight.
        </h2>
        <p className="mt-4 text-[#bbb] text-base leading-relaxed max-w-lg">
          Create an account and Boardroom books the slot, holds the record and
          hands you the minutes. Fifteen minutes, once a week.
        </p>
        <button
          type="button"
          onClick={openAuth}
          className="mt-7 w-full sm:w-auto h-12 px-6 rounded-md bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium transition-colors flex items-center justify-center gap-2"
          data-testid="landing-closing-cta-button"
        >
          Create your free account
          <ArrowUpRight size={16} />
        </button>
        <p className="mt-4 text-xs text-[#8a8a8a] font-mono uppercase tracking-widest">
          Takes 30 seconds · cancel anything, anytime
        </p>
      </section>

      {/* ---------------- Sticky mobile CTA ---------------- */}
      <div
        className="md:hidden fixed inset-x-0 bottom-0 z-40 glass border-t border-white/10 px-4 pt-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}
        data-testid="landing-sticky-cta"
      >
        <button
          type="button"
          onClick={openAuth}
          className="w-full h-12 rounded-md bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium transition-colors flex items-center justify-center gap-2"
          data-testid="landing-sticky-cta-button"
        >
          Create your free account
          <ArrowUpRight size={16} />
        </button>
      </div>
    </div>
  );
}
